<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import * as turf from '@turf/turf';

mapboxgl.accessToken =
    'pk.eyJ1IjoiYWFyY2lsYTE4IiwiYSI6ImNsM2V6YWdscjBuZDMzY3A3M2FtMDl6YWgifQ.adh5HQ_qhIy3cjG1a-pSAQ';

const props = defineProps({
    center: {
        type: Array,
        default: () => [-76.3193821, 3.39877],
    },
    id: {
        type: String,
        default: 'farm-boundary-map',
    },
    fullPage: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['selectedLocation', 'selectedArea']);

const mapContainer = ref(null);
const mapReady = ref(false);
const mode = ref('location');
const selectedLocation = ref(null);
const areaSquareMeters = ref(0);
const gpsLoading = ref(false);
const gpsError = ref('');

let map = null;
let marker = null;
let draw = null;
let geocoder = null;
let mobilePointerStart = null;
let suppressMapClickUntil = 0;
let mapResizeObserver = null;
let resizeFrame = null;

const hasLocation = computed(() => selectedLocation.value !== null);
const hasBoundary = computed(() => areaSquareMeters.value > 0);
const canDraw = computed(() => mapReady.value && hasLocation.value);

const instruction = computed(() => {
    if (hasBoundary.value) {
        return 'Linderos listos. Revisa el área o reinicia el mapa para comenzar nuevamente.';
    }

    if (mode.value === 'boundary') {
        return 'Toca cada esquina del terreno y vuelve al primer punto para cerrar el polígono.';
    }

    if (hasLocation.value) {
        return 'Ubicación lista. Toca nuevamente el mapa para comenzar a marcar los linderos.';
    }

    return 'En el teléfono, el primer toque ubica la finca. También puedes usar el GPS.';
});

const formattedArea = computed(() => {
    if (!areaSquareMeters.value) {
        return '';
    }

    const hectares = areaSquareMeters.value / 10000;
    return `${hectares.toLocaleString('es-CO', { maximumFractionDigits: 4 })} ha · ${areaSquareMeters.value.toLocaleString('es-CO', { maximumFractionDigits: 2 })} m²`;
});

function setLocation(longitude, latitude, zoom = 17, recenter = true) {
    const coordinates = [Number(longitude), Number(latitude)];
    selectedLocation.value = coordinates;
    gpsError.value = '';

    if (!marker) {
        marker = new mapboxgl.Marker({ color: '#03662E' });
    }

    marker.setLngLat(coordinates).addTo(map);
    if (recenter) {
        map.flyTo({ center: coordinates, zoom, essential: true });
    }
    emit('selectedLocation', coordinates);
    queueMobileBoundaryMode();
}

function isMobileViewport() {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
}

function resizeMap() {
    if (!map) {
        return;
    }

    if (resizeFrame) {
        window.cancelAnimationFrame(resizeFrame);
    }

    resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = null;
        map.resize();
    });
}

function dismissMobileKeyboard() {
    if (!isMobileViewport()) {
        return;
    }

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement !== document.body) {
        activeElement.blur();
    }
}

function queueMobileBoundaryMode() {
    if (!isMobileViewport()) {
        return;
    }

    dismissMobileKeyboard();
    window.setTimeout(() => {
        if (draw && hasLocation.value) {
            startBoundary();
        }
    }, 0);
}

function isMapControlTarget(target) {
    return target instanceof Element && target.closest('.mapboxgl-ctrl') !== null;
}

function handleMapPointerDown(event) {
    dismissMobileKeyboard();

    if (!isMobileViewport() || event.pointerType !== 'touch' || mode.value !== 'location' || isMapControlTarget(event.target)) {
        mobilePointerStart = null;
        return;
    }

    mobilePointerStart = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        time: Date.now(),
    };
}

function handleMapPointerUp(event) {
    if (
        !mobilePointerStart ||
        event.pointerId !== mobilePointerStart.pointerId ||
        mode.value !== 'location' ||
        isMapControlTarget(event.target)
    ) {
        mobilePointerStart = null;
        return;
    }

    const movement = Math.hypot(
        event.clientX - mobilePointerStart.x,
        event.clientY - mobilePointerStart.y
    );
    const elapsed = Date.now() - mobilePointerStart.time;
    mobilePointerStart = null;

    if (movement > 14 || elapsed > 1000 || !map) {
        return;
    }

    const canvasBounds = map.getCanvas().getBoundingClientRect();
    const coordinates = map.unproject([
        event.clientX - canvasBounds.left,
        event.clientY - canvasBounds.top,
    ]);

    event.preventDefault();
    event.stopPropagation();
    suppressMapClickUntil = Date.now() + 700;
    setLocation(coordinates.lng, coordinates.lat, map.getZoom(), false);
}

function clearBoundary() {
    if (draw) {
        draw.deleteAll();
        draw.changeMode('simple_select');
    }

    areaSquareMeters.value = 0;
    emit('selectedArea', { locations: [], area: 0 });
}

function activateLocationMode() {
    mode.value = 'location';
    clearBoundary();
}

function startBoundary() {
    if (!canDraw.value) {
        return;
    }

    clearBoundary();
    mode.value = 'boundary';
    draw.changeMode('draw_polygon');
}

function resetMapCreation() {
    dismissMobileKeyboard();
    clearBoundary();

    gpsError.value = '';

    if (draw && hasLocation.value) {
        mode.value = 'boundary';
        draw.changeMode('draw_polygon');
    }
}

function updateBoundary() {
    const data = draw.getAll();

    if (!data.features.length) {
        areaSquareMeters.value = 0;
        emit('selectedArea', { locations: [], area: 0 });
        return;
    }

    const area = Math.round(turf.area(data) * 100) / 100;
    const locations = data.features[0].geometry.coordinates[0];
    areaSquareMeters.value = area;
    mode.value = 'complete';
    emit('selectedArea', { locations, area });

    const bounds = turf.bbox(data);
    map.fitBounds(bounds, { padding: 60, maxZoom: 18, duration: 700 });
}

function useGps() {
    gpsError.value = '';

    if (!navigator.geolocation) {
        gpsError.value = 'Este dispositivo no ofrece ubicación GPS.';
        return;
    }

    gpsLoading.value = true;
    navigator.geolocation.getCurrentPosition(
        (position) => {
            gpsLoading.value = false;
            activateLocationMode();
            setLocation(position.coords.longitude, position.coords.latitude, 18);
        },
        (error) => {
            gpsLoading.value = false;
            gpsError.value = error.code === 1
                ? 'Permite el acceso a tu ubicación para usar el GPS.'
                : 'No pudimos obtener tu ubicación. Intenta nuevamente o selecciónala en el mapa.';
        },
        {
            enableHighAccuracy: true,
            timeout: 12000,
            maximumAge: 30000,
        }
    );
}

onMounted(() => {
    const initialCenter = props.center.length === 2 ? props.center : [-76.3193821, 3.39877];

    map = new mapboxgl.Map({
        container: mapContainer.value,
        style: 'mapbox://styles/mapbox/satellite-streets-v11',
        center: initialCenter,
        zoom: 15,
        cooperativeGestures: false,
    });

    if (typeof ResizeObserver !== 'undefined') {
        mapResizeObserver = new ResizeObserver(resizeMap);
        mapResizeObserver.observe(mapContainer.value);
    }
    window.addEventListener('resize', resizeMap);
    window.addEventListener('orientationchange', resizeMap);

    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(resizeMap);
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.addControl(new mapboxgl.FullscreenControl(), 'top-right');

    geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl,
        marker: false,
        placeholder: 'Buscar lugar',
    });
    map.addControl(geocoder, 'top-left');
    geocoder.on('result', (event) => {
        const coordinates = event.result.center || event.result.geometry.coordinates;
        activateLocationMode();
        setLocation(coordinates[0], coordinates[1], 17);
    });

    map.on('load', () => {
        draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: {},
        });
        map.addControl(draw);
        mapReady.value = true;

        map.on('draw.create', updateBoundary);
        map.on('draw.update', updateBoundary);
        map.on('draw.delete', updateBoundary);
        resizeMap();
    });

    map.on('click', (event) => {
        dismissMobileKeyboard();
        if (Date.now() < suppressMapClickUntil) {
            return;
        }

        if (mode.value === 'location') {
            setLocation(event.lngLat.lng, event.lngLat.lat, map.getZoom(), false);
        }
    });
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', resizeMap);
    window.removeEventListener('orientationchange', resizeMap);
    if (mapResizeObserver) {
        mapResizeObserver.disconnect();
        mapResizeObserver = null;
    }
    if (resizeFrame) {
        window.cancelAnimationFrame(resizeFrame);
        resizeFrame = null;
    }
    if (map) {
        map.remove();
    }
});
</script>

<template>
    <section class="w-full min-w-0 space-y-3">
        <div class="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
            <button
                type="button"
                class="farm-map-action farm-map-mode-action"
                :class="{ 'farm-map-action-active': mode === 'location' }"
                @click="activateLocationMode"
            >
                <span class="material-symbols-outlined">location_on</span>
                Ubicar finca
            </button>
            <button
                type="button"
                class="farm-map-action farm-map-mode-action"
                :class="{ 'farm-map-action-active': mode === 'boundary' }"
                :disabled="!canDraw"
                @click="startBoundary"
            >
                <span class="material-symbols-outlined">polyline</span>
                {{ hasBoundary ? 'Redibujar linderos' : 'Delinear linderos' }}
            </button>
            <button
                type="button"
                class="farm-map-action farm-map-gps md:hidden"
                :disabled="gpsLoading"
                @click="useGps"
            >
                <span class="material-symbols-outlined">my_location</span>
                {{ gpsLoading ? 'Buscando GPS…' : 'Usar mi ubicación GPS' }}
            </button>
        </div>

        <p class="text-sm text-enriqueto_green">{{ instruction }}</p>
        <p v-if="gpsError" class="p-3 text-sm text-red-700 bg-red-50 rounded-xl" role="alert">
            {{ gpsError }}
        </p>

        <div
            :id="id"
            ref="mapContainer"
            class="w-full overflow-hidden border-2 farm-creation-map rounded-2xl border-light_green"
            :class="{
                'farm-creation-map-full': fullPage,
                'farm-map-location-locked': hasLocation,
                'farm-map-delineating': mode === 'boundary'
            }"
            @pointerdown.capture="handleMapPointerDown"
            @pointerup.capture="handleMapPointerUp"
        ></div>

        <button
            type="button"
            class="w-full farm-map-reset sm:w-auto"
            :disabled="!hasLocation"
            @click="resetMapCreation"
        >
            <span class="material-symbols-outlined">restart_alt</span>
            Borrar puntos y redibujar linderos
        </button>

        <div v-if="hasBoundary" class="flex items-center justify-between gap-3 p-3 bg-light_yellow rounded-xl">
            <div>
                <p class="text-sm font-bold text-enriqueto_green">Área calculada</p>
                <p class="text-sm text-enriqueto_green">{{ formattedArea }}</p>
            </div>
            <button type="button" class="text-sm text-red-700 underline" @click="startBoundary">
                Volver a dibujar
            </button>
        </div>
    </section>
</template>

<style scoped>
.farm-creation-map {
    box-sizing: border-box;
    height: min(58vh, 620px);
    max-width: 100%;
    min-width: 0;
    min-height: 440px;
    position: relative;
    width: 100%;
}

.farm-creation-map-full {
    height: calc(100vh - 250px);
    max-height: none;
    min-height: 640px;
}

.farm-map-action {
    align-items: center;
    border: 1px solid #03662E;
    border-radius: 0.75rem;
    color: #03662E;
    display: flex;
    gap: 0.35rem;
    justify-content: center;
    padding: 0.65rem 0.9rem;
}

.farm-map-action:hover,
.farm-map-action-active {
    background: #03662E;
    color: white;
}

.farm-map-action:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

.farm-map-gps {
    background: #F3E022;
    border-color: #F3E022;
    color: #03662E;
    font-weight: 700;
}

.farm-map-reset {
    align-items: center;
    background: rgba(196, 196, 196, 0.24);
    border: 1px solid rgba(58, 58, 58, 0.28);
    border-radius: 0.75rem;
    color: #03662E;
    display: flex;
    font-weight: 700;
    gap: 0.35rem;
    justify-content: center;
    padding: 0.7rem 1rem;
}

.farm-map-reset:hover:not(:disabled) {
    background: rgba(196, 196, 196, 0.42);
    border-color: #03662E;
}

.farm-map-reset:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

@media (max-width: 767px) {
    .farm-map-mode-action {
        display: none;
    }
}

@media (max-width: 640px) {
    .farm-creation-map {
        height: 60vh;
        min-height: 460px;
    }

    .farm-creation-map-full {
        height: 68vh;
        min-height: 520px;
    }
}

@media (min-width: 768px) {
    .farm-map-gps {
        display: none !important;
    }
}
</style>

<style>
@media (max-width: 767px) {
    .farm-creation-map.farm-map-location-locked .mapboxgl-ctrl-top-left {
        display: none;
    }
}
</style>
